import React, { useEffect, useState } from 'react';

import { BaseControlProps } from '../../models/BaseControlProps';
import { PayslipsAPIService } from '../../services/PayslipsAPIService';
import { DataTable, Pager, Span } from '@tag/tag-components-react-v4';
import { PayslipGetRequest } from '../../models/PayslipGetRequest';
import { useAuth } from '../../services/AuthContext';
import { UtilService } from '../../services/UtilService';
import { useMessage } from '../../components/Common/MessageContext/MessageContext';
import { Payslip } from '../../models/Payslip';
import { Box, Button, Card, CardContent, Divider, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Skeleton, Typography, useMediaQuery, useTheme } from '@mui/material';
import moment from 'moment';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PdfModal from '../../components/PDF/PDFModal';
import { PayslipDocumentGetRequest } from '../../models/PayslipDocumentGetRequest';


const PaySlip = (props: BaseControlProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const pageSize = 15;
    const currentYear = moment().year();
    const [year, setYear] = useState<string>(moment().format('YYYY'));
    const yearOptions = Array.from(new Array(20), (val, index) => currentYear - index);
    const [loading, isLoading] = useState<boolean>(true);
    const [month, setMonth] = useState<string>(moment().format('MM').toLowerCase());
    const [ pdfVisible, setPdfVisible] = useState<boolean>(false);
    const [ base64PDF, setBase64PDF] = useState<string>('');
    const [payslipDate, setPayslipDate] = useState<string>('');
    const [scopeGet, applyGet] = useState<PayslipGetRequest>({
        monthNumberFilter: month,
        yearFilter: year,
        pageNo: 0,
        pageSize: pageSize
    });
    const [scopeDocument, applyDocument] = useState<PayslipDocumentGetRequest>({
        encryptedPayslipId: '',
        additionalRunNumber: 1,
        payslipDate: '',
    });
    const [payslips, setPayslips] = useState<Payslip[]>([]);
    const [page, setPage] = useState(1);
    const [pageCount, setPageCount] = useState(0);
    const authContext = useAuth();
    const { showMessage } = useMessage();
    const payslipsService = new PayslipsAPIService();


    useEffect(() => {
        const getPayslipsAsync = async () => {
            await getPayslips(month, year);
        }
        getPayslipsAsync();
    }, []);

    const getPayslips = (month: string, year: string) => {
        isLoading(true);
        scopeGet.monthNumberFilter = month;
        scopeGet.yearFilter = year;
        applyGet(scopeGet);
        payslipsService.getPayslips(scopeGet, authContext.token).then((response) => {
            if (response.error) {
                if(props.onMessageNeeded) {
                    props.onMessageNeeded(UtilService.getErrorMessageInfo(response.error));
                }
                showMessage(response.error, 'error');
            }
            else {
                setPayslips(response.response?.payslipList ?? []);
                setPage(page);
                const pageCount =
                    response.response && response.response?.payslipList.length > 0
                        ? Math.ceil(response.response.totalCount / pageSize)
                        : 0;
                setPageCount(pageCount && pageCount > 0 ? pageCount : 0);
            }
            isLoading(false);
        })
    }

    const getPayslipPdf = (payslipId: string, date: string) => {
        scopeDocument.encryptedPayslipId = payslipId;
        scopeDocument.payslipDate = date;
        applyDocument(scopeDocument);
        setPdfVisible(true);
        payslipsService.getPayslipDocument(scopeDocument, authContext.token).then((response) => {
            if (response.error) {
                if(props.onMessageNeeded) {
                    props.onMessageNeeded(UtilService.getErrorMessageInfo(response.error));
                }
                showMessage(response.error, 'error');
            }
            else {
                setBase64PDF(response.response?.document ?? '');
                setPayslipDate(date);
            }
        })
    }

    const handleMonthChange = async (e: any) => {
        const monthInput = e.target.value;
        setMonth(monthInput);
        await getPayslips(monthInput, year);
    }

    const handleYearChange = async (e: any) => {
        const yearInput = e.target.value.toString(); 
        setYear(yearInput);
        await getPayslips(month, yearInput);
    }

    function renderViewButton(encrytptedPayslipId: string, payslipDate: string) {
        if (isMobile) {
            return (
                <Button
                    variant="outlined"
                    onClick={() => {
                        getPayslipPdf(encrytptedPayslipId, payslipDate);
                    }}
                    endIcon={<PictureAsPdfIcon />}
                >
                    View
                </Button>
            )
        } else {
            return (
              <IconButton
                aria-label='payslip-pdf'
                size='small'
                color='primary'
                onClick={() => {
                    getPayslipPdf(encrytptedPayslipId, payslipDate);
                }}
              >
                <PictureAsPdfIcon />
              </IconButton>
            );
        }
    }

    const columns = React.useMemo(
        () => [
        { 
            accessorKey: 'payslipDate', 
            header: 'Payslip Date', 
            meta: { dataType: 'date' },
            cell: ({ row }: any) => {
                const date = row.original.payslipDate;
                return date == '1900-01-01T00:00:00'
                    ? '-'
                    : moment(date).format('DD-MM-YY');
            },
        },
        // { accessorKey: 'payGroup', header: 'Pay Group' },
        { accessorKey: 'pensionSchema', header: 'Scheme' },
        { 
            accessorKey: 'grossPay', 
            header: 'Gross Pay', 
            meta: { dataType: 'decimal' },
            cell: ({row}: any) => {
                return row.original.grossPay ? Number(row.original.grossPay).toFixed(2) : '';
            }
        },
        { 
            accessorKey: 'netPay', 
            header: 'Net Pay', 
            meta: { dataType: 'decimal' },
            cell: ({row}: any) => {
                return row.original.netPay ? Number(row.original.netPay).toFixed(2) : '';
            }
        },
        {
            accessorKey: 'view',
            header: () => <div style={{ textAlign: 'center' }}>View PDF</div>,
            cell: ({ row }: any) => {
                return renderViewButton(row.original.encrytptedPayslipId, row.original.payslipDate)
            },
            meta: {
                getCellProps: () => {
                return { style: { textAlign: 'center' } };
                },
            },
        },
        ],
        [],
    );

    const handlePageChange = async (e: any) => {
        setPage(e.page);

        await getPayslips(month, year);
    };

    const renderheaderView = () => {
        return (
            <Box sx={{ width: '100%', pt: 2, pb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography component="h2" variant="h5" gutterBottom>
                        Payslips
                    </Typography>
                </Box>
                <Divider  sx={{ mb: 2 }} />
                <Grid container spacing={2} alignItems="center">
                    {/* Month Filter */}
                    <Grid item xs={12} sm={5} md={4}>
                        <FormControl fullWidth variant='outlined'>
                            <InputLabel id="month-select-label">Month</InputLabel>
                            <Select
                                labelId='month-select-label'
                                id="month-select"
                                label="Month"
                                value={month}
                                onChange={handleMonthChange}
                                inputProps={{
                                    'aria-labelledby': 'month-select-label',
                                    'aria-controls': undefined // Explicitly remove aria-controls
                                }}
                                SelectDisplayProps={{
                                    'aria-controls': undefined // Also remove from display component
                                }}
                            >
                                <MenuItem value='01'>January</MenuItem>
                                <MenuItem value='02'>February</MenuItem>
                                <MenuItem value='03'>March</MenuItem>
                                <MenuItem value='04'>April</MenuItem>
                                <MenuItem value='05'>May</MenuItem>
                                <MenuItem value='06'>June</MenuItem>
                                <MenuItem value='07'>July</MenuItem>
                                <MenuItem value='08'>August</MenuItem>
                                <MenuItem value='09'>September</MenuItem>
                                <MenuItem value='10'>October</MenuItem>
                                <MenuItem value='11'>November</MenuItem>
                                <MenuItem value='12'>December</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Year Filter */}
                    <Grid item xs={12} sm={5} md={4}>
                        <FormControl fullWidth variant='outlined'>
                            <InputLabel id="year-select-label" >Year</InputLabel>
                            <Select
                                labelId="year-select-label"
                                id="year-select"
                                label="Year"
                                value={year}
                                onChange={handleYearChange}
                                inputProps={{
                                    'aria-labelledby': 'year-select-label',
                                    'aria-controls': undefined // Explicitly remove aria-controls
                                }}
                                SelectDisplayProps={{
                                    'aria-controls': undefined // Also remove from display component
                                }}
                            >
                                {yearOptions.map((yearOption) => (
                                    <MenuItem key={yearOption} value={yearOption}>
                                    {yearOption}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Filter Button */}
                    <Grid item xs={12} sm={2} md={4}>
                        <Button
                            variant='contained'
                            color='primary'
                            size='large'
                            style={{ minWidth: '80px' }}
                            onClick={() => {
                                setPage(0);
                                scopeGet.pageNo = 0;
                                applyGet(scopeGet);
                                getPayslips(month, year);
                            }}
                        >
                            Filter
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        );
    } 

    const EmptyState = () => (
        <Box 
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '150px',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '4px',
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No payslips found
          </Typography>
        </Box>
    );

    // Loading skeleton for mobile
    const MobileLoadingSkeleton = () => (
        <Box sx={{ mb: 2 }}>
        {[...Array(3)].map((_, index) => (
            <Card key={index} sx={{ mb: 2 }}>
            <CardContent>
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="40%" height={20} />
                <Box display="flex" justifyContent="flex-end">
                <Skeleton variant="rectangular" width={100} height={36} />
                </Box>
            </CardContent>
            </Card>
        ))}
        </Box>
    );

    if (isMobile) {
        return (
            <>
                {renderheaderView()}  

                <div>
                    {loading ? (
                    <MobileLoadingSkeleton />
                        ) : payslips.length === 0 ? (
                        <EmptyState />
                        ) : (
                        payslips.map((payslip) => (
                            <Card key={payslip.payslipDate} sx={{ mb: 1 }}>
                                <CardContent>
                                    <Typography component="h2" variant="subtitle2">
                                    Paslip Date:
                                    {payslip.payslipDate === '1900-01-01T00:00:00' 
                                        ? '-' 
                                        : moment(payslip.payslipDate).format('DD-MM-YY HH:mm')}
                                    </Typography>
                                    <Typography component="h2" variant="subtitle2" color="text.secondary">
                                    Pay Group: {payslip.payGroup}
                                    </Typography>
                                    <Typography component="h2" variant="subtitle2" color="text.secondary">
                                    Scheme: {payslip.pensionSchema}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                    Net Pay: {payslip.netPay ? Number(payslip.netPay).toFixed(2) : '-'} 
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                    Gross Pay: {payslip.grossPay ? Number(payslip.grossPay).toFixed(2) : '-'}
                                    </Typography>
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    {renderViewButton(payslip.encrytptedPayslipId, payslip.payslipDate)}
                                    </div>
                                </CardContent>
                            </Card>
                        ))
                    )}
                   {!loading && payslips.length > 0 && (
                        <Pager
                            page={page}
                            pageCount={pageCount}
                            onPageRequest={(e) => handlePageChange(e.page)}
                            style={{ justifyContent: 'center', padding: '5px' }}
                        />
                    )}
                </div>

                <PdfModal base64Pdf={base64PDF} visible={pdfVisible} payDate={payslipDate} handleClose={() => { setPdfVisible(false); setBase64PDF(''); }} />
            </>
        );
    }

    return(
        <>
            {renderheaderView()}

            <div style={{ background: '#fff' }}>
                <div style={{ height: 'auto' }}>
                    <DataTable 
                        isLoading={loading} 
                        loadingElement={<Span accent="blue">Loading ...</Span>} 
                        accent='blue' 
                        columns={columns} 
                        data={payslips} 
                        tableProps={{
                            role: 'presentation'
                        }}
                        getRowProps={(row) => ({
                            style: { fontSize: '16px' },
                        })}
                    />
                </div>
                <Pager
                    page={page}
                    pageCount={pageCount}
                    onPageRequest={(e) => handlePageChange(e.page)}
                    style={{ justifyContent: 'center', padding: '5px', borderBottom: '1px solid #e2ecef' }}
                />
            </div>

            <PdfModal base64Pdf={base64PDF} payDate={payslipDate} visible={pdfVisible} handleClose={() => { setPdfVisible(false); setBase64PDF(''); }} />
        </>
    )
}
export default PaySlip;